import {Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faCircleInfo, faSpinnerThird, faTriangleExclamation} from '@fortawesome/pro-solid-svg-icons';
import {
  BicButtonComponent,
  BicPopupComponent,
  BicToastService,
  TooltipDirective,
} from '@buildinginformationcloud/bic-ui-components';
import {concat, Observable, of} from 'rxjs';
import {last} from 'rxjs/operators';
import {
  ProjectShareTarget,
  ReferencedRuleset,
  SharedProject,
  WorkflowService,
} from '../../../../services/workflow.service';
import {RulesetService} from '../../../../services/ruleset.service';
import {serverErrorMessage} from '../server-error-message';

/** Was geteilt wird: ein Prüflauf (WorkflowDefinition) oder ein myCheck (Prüfregelset). */
export type ProjectShareEntity = 'check-run' | 'ruleset';

/**
 * Teilen-Dialog für normale Nutzer: einen Prüflauf oder ein myCheck (Prüfregelset) live in weitere
 * Projekte derselben Organisation verlinken bzw. die Freigabe wieder aufheben.
 *
 * <p>Nicht zu verwechseln mit der zentralen Bereitstellung (Katalog-Einreichung,
 * {@code catalog-submission-prompt}; Ziele legt das Admin Center fest). Hier gibt es nur Projekte,
 * und es entsteht keine Kopie.
 *
 * <p>Wer welches Projekt wählen darf, entscheidet das Backend ({@link ProjectShareTarget#allowed}) –
 * das Frontend kennt keine Rollennamen. Der Dialog speichert selbst (Differenz aus neu gewählten und
 * abgewählten Projekten) und meldet das Ergebnis über {@link saved}.
 */
@Component({
  selector: 'app-share-with-projects-prompt',
  standalone: true,
  imports: [FaIconComponent, BicPopupComponent, BicButtonComponent, TooltipDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './share-with-projects-prompt.component.html',
})
export class ShareWithProjectsPromptComponent implements OnInit {
  /** ID des Prüflaufs bzw. des Regelsets. */
  @Input({required: true}) definitionId!: string;
  @Input({required: true}) checkName!: string;
  @Input() entity: ProjectShareEntity = 'check-run';
  /** Eigentümerprojekt – geteilt wird immer aus ihm heraus. */
  @Input({required: true}) projectId!: number;

  @Output() closed = new EventEmitter<void>();
  /** Projekte, in denen der myCheck nach dem Speichern sichtbar ist (Eigentümer zuerst). */
  @Output() saved = new EventEmitter<SharedProject[]>();

  private readonly flowService = inject(WorkflowService);
  private readonly rulesetService = inject(RulesetService);
  private readonly toastService = inject(BicToastService);

  protected readonly faSpinnerThird = faSpinnerThird;
  protected readonly faTriangleExclamation = faTriangleExclamation;
  protected readonly faCircleInfo = faCircleInfo;

  protected readonly noPermissionTooltip =
    'Dafür fehlen Ihnen die Rechte: Organisations-Admin und in beiden Projekten mindestens Bearbeiter, ' +
    'oder Organisations-Bearbeiter bzw. „Projekte bearbeiten" und in beiden Projekten Admin bzw. „myCheck verwalten".';

  protected targets: ProjectShareTarget[] = [];
  protected loading = true;
  protected loadFailed = false;
  protected saving = false;
  /** Gewünschter Zustand: Projekte, in die der myCheck nach dem Speichern verlinkt sein soll. */
  protected selected = new Set<number>();

  /**
   * Zweiter Schritt beim Teilen eines Prüflaufs: seine myChecks, die in mindestens einem der neu
   * gewählten Projekte fehlen. Ohne sie erschiene der Prüflauf dort ohne auswählbare Prüfregeln.
   */
  protected rulesetStep = false;
  /** Eigene myChecks, die mitgeteilt werden können – mit den Zielprojekten, in denen sie fehlen. */
  protected missingRulesets: { ruleset: ReferencedRuleset; missingIn: string }[] = [];
  /** myChecks eines dritten Projekts – nur dort teilbar, der Nutzer muss es wissen. */
  protected foreignRulesets: { ruleset: ReferencedRuleset; missingIn: string }[] = [];
  protected selectedRulesets = new Set<string>();
  protected loadingRulesets = false;

  protected get isRuleset(): boolean {
    return this.entity === 'ruleset';
  }

  /** Die drei Aufrufe je Entität – beide Backends haben dieselbe Form. */
  private get api() {
    return this.isRuleset
      ? {
        targets: (id: string, pid: number) => this.rulesetService.listProjectShareTargets(id, pid),
        share: (id: string, pid: number, ids: number[]) => this.rulesetService.shareWithProjects(id, pid, ids),
        unshare: (id: string, target: number, pid: number) => this.rulesetService.unshareFromProject(id, target, pid),
      }
      : {
        targets: (id: string, pid: number) => this.flowService.listProjectShareTargets(id, pid),
        share: (id: string, pid: number, ids: number[]) =>
          this.flowService.shareWithProjects(id, pid, ids, [...this.selectedRulesets]),
        unshare: (id: string, target: number, pid: number) => this.flowService.unshareFromProject(id, target, pid),
      };
  }

  ngOnInit(): void {
    this.api.targets(this.definitionId, this.projectId).subscribe({
      next: targets => {
        this.targets = targets;
        this.selected = new Set(targets.filter(t => t.linked).map(t => t.projectId));
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.loadFailed = true;
      },
    });
  }

  protected isSelected(target: ProjectShareTarget): boolean {
    return this.selected.has(target.projectId);
  }

  protected toggle(target: ProjectShareTarget): void {
    if (!target.allowed || this.saving) return;
    if (this.selected.has(target.projectId)) {
      this.selected.delete(target.projectId);
    } else {
      this.selected.add(target.projectId);
    }
  }

  protected get toShare(): ProjectShareTarget[] {
    return this.targets.filter(t => !t.linked && this.selected.has(t.projectId));
  }

  protected get toUnshare(): ProjectShareTarget[] {
    return this.targets.filter(t => t.linked && !this.selected.has(t.projectId));
  }

  protected get hasChanges(): boolean {
    return this.toShare.length > 0 || this.toUnshare.length > 0;
  }

  protected get hasAllowedTarget(): boolean {
    return this.targets.some(t => t.allowed);
  }

  /**
   * Kein Tooltip. Bewusst `undefined` statt `''`: die TooltipDirective der UI-Bibliothek blendet
   * sich nur bei `undefined` aus – bei einem leeren String erscheint beim Hovern ein leerer Pfeil.
   */
  protected readonly noTooltip = undefined as unknown as string;

  /** Warum „Speichern" gesperrt ist – nur solange nichts geändert wurde. */
  protected get saveDisabledTooltip(): string {
    const label = this.isRuleset ? 'Der myCheck' : 'Der Prüflauf';
    if (this.loadFailed) {
      return 'Die Projekte konnten nicht geladen werden. Bitte versuchen Sie es erneut.';
    }
    if (this.targets.length === 0) {
      return `Es gibt kein Projekt, mit dem ${this.isRuleset ? 'der myCheck' : 'der Prüflauf'} geteilt werden kann.`;
    }
    if (!this.hasAllowedTarget) {
      return this.noPermissionTooltip;
    }
    const allAllowedLinked = this.targets.filter(t => t.allowed).every(t => t.linked);
    if (allAllowedLinked) {
      return `${label} ist bereits mit allen Projekten geteilt, für die Sie die Rechte haben. `
        + 'Entfernen Sie einen Haken, um eine Freigabe aufzuheben.';
    }
    return 'Wählen Sie ein Projekt aus oder entfernen Sie einen Haken, um die Freigaben zu ändern.';
  }

  protected onClose(): void {
    if (this.saving) return;
    this.closed.emit();
  }

  /**
   * Erst teilen, dann aufheben – beides auf einmal gibt es im Backend nicht. Das letzte Ergebnis
   * enthält den vollständigen Stand, weil jeder Aufruf alle sichtbaren Projekte zurückgibt – deshalb
   * laufen die Aufhebungen nacheinander: parallel wäre die zuletzt eintreffende Antwort nicht
   * zwingend die des zuletzt verarbeiteten Aufrufs.
   */
  protected onConfirm(): void {
    if (!this.hasChanges || this.saving) return;
    // Prüflauf mit neuen Zielprojekten: erst klären, ob seine myChecks mitgeteilt werden sollen.
    if (!this.isRuleset && !this.rulesetStep && this.toShare.length > 0) {
      this.prepareRulesetStep();
      return;
    }
    this.save();
  }

  protected toggleRuleset(id: string): void {
    if (this.saving) return;
    if (this.selectedRulesets.has(id)) {
      this.selectedRulesets.delete(id);
    } else {
      this.selectedRulesets.add(id);
    }
  }

  protected backToTargets(): void {
    if (this.saving) return;
    this.rulesetStep = false;
  }

  protected get rulesetConfirmLabel(): string {
    return this.selectedRulesets.size > 0 ? 'Mit myChecks teilen' : 'Nur Prüflauf teilen';
  }

  /**
   * Lädt die myChecks des Prüflaufs und zeigt den zweiten Schritt nur, wenn in einem der neuen
   * Zielprojekte wirklich etwas fehlt – sonst wird direkt gespeichert. Scheitert das Laden, wird
   * nicht geteilt: ohne die Rückfrage erschiene der Prüflauf womöglich ohne Prüfregeln. Der Dialog
   * bleibt offen, der Nutzer kann es erneut versuchen.
   */
  private prepareRulesetStep(): void {
    this.loadingRulesets = true;
    const newTargets = this.toShare;
    this.flowService.listReferencedRulesetsForShare(this.definitionId, this.projectId).subscribe({
      next: rulesets => {
        this.loadingRulesets = false;
        const withGaps = rulesets
          .map(ruleset => ({
            ruleset,
            missingIn: newTargets
              .filter(t => !ruleset.linkedProjectIds.includes(t.projectId) && t.projectId !== ruleset.ownerProjectId)
              .map(t => t.name)
              .join(', '),
          }))
          .filter(entry => entry.missingIn.length > 0);
        this.missingRulesets = withGaps.filter(e => e.ruleset.shareable);
        this.foreignRulesets = withGaps.filter(e => !e.ruleset.shareable);
        if (withGaps.length === 0) {
          this.save();
          return;
        }
        this.selectedRulesets = new Set(this.missingRulesets.map(e => e.ruleset.id));
        this.rulesetStep = true;
      },
      error: err => {
        this.loadingRulesets = false;
        this.toastService.error(serverErrorMessage(err,
          'Die myChecks des Prüflaufs konnten nicht geladen werden. Es wurde nichts geteilt.'));
      },
    });
  }

  private save(): void {
    this.saving = true;

    const share$: Observable<SharedProject[] | null> = this.toShare.length > 0
      ? this.api.share(this.definitionId, this.projectId, this.toShare.map(t => t.projectId))
      : of(null);
    const unshareIds = this.toUnshare.map(t => t.projectId);

    share$.subscribe({
      next: shared => {
        if (unshareIds.length === 0) {
          this.finish(shared ?? []);
          return;
        }
        concat(...unshareIds.map(id => this.api.unshare(this.definitionId, id, this.projectId)))
          .pipe(last())
          .subscribe({
            next: result => this.finish(result),
            error: err => this.fail(err, 'Die Freigabe konnte nicht für alle Projekte aufgehoben werden.'),
          });
      },
      error: err => this.fail(err, 'Teilen fehlgeschlagen.'),
    });
  }

  private finish(result: SharedProject[]): void {
    this.saving = false;
    this.toastService.success(result.length > 1
      ? 'Freigaben gespeichert.'
      : (this.isRuleset ? 'myCheck wird nicht mehr geteilt.' : 'Prüflauf wird nicht mehr geteilt.'));
    this.saved.emit(result);
  }

  private fail(err: unknown, fallback: string): void {
    this.saving = false;
    this.toastService.error(serverErrorMessage(err, fallback));
  }
}
